import { toUrlFormat } from '../../../utils/treeVisitors';
import { isRelativeLinkToMdFile } from '../../../utils/locationUtils/relativeMarkdownLinks';
import { isHashLink } from '../../../utils/locationUtils';
import { FLAGS, REGEXES } from './consts';
import { getArticlesSectionGuideWithTagLink } from '../../../utils/treeVisitors/createGuideTags';
import { extractTag } from '../../../utils/flattenTree/utils/extractors';

const getRelativeMdLinkWithoutPathTraversal = (relativeMdLink) =>
  relativeMdLink.replace(REGEXES.PATH_TRAVERSAL, '');

const getRelevantFileByUrl = (serviceJsonPaths, link, currentFilePath) => {
  const linkKeys = link.replace('/guides', '').split('/');
  const [categoryPart, sectionPart] = linkKeys;
  const category = categoryPart === 'all' ? 'all-apis' : categoryPart;
  const isArticlesSectionGuide = link.split('/')[0] === 'guides';
  const isLinkToFirstLevelMd =
    linkKeys.length === 2 && sectionPart.endsWith('.md');

  if (isArticlesSectionGuide) {
    return serviceJsonPaths.find((path) => path.endsWith('guides/guides.json'));
  }

  if (isLinkToFirstLevelMd) {
    const matchCategoryFile = serviceJsonPaths.find((filePath) => {
      return filePath.endsWith(`${category}.service.json`);
    });
    return matchCategoryFile;
  }

  const matchCategoryAndSectionFile = serviceJsonPaths.find((filePath) => {
    return filePath.endsWith(`${category}/${sectionPart}.service.json`);
  });

  if (matchCategoryAndSectionFile) {
    return matchCategoryAndSectionFile;
  }

  const matchSectionFile = serviceJsonPaths.find((filePath) => {
    return (
      filePath !== currentFilePath &&
      filePath.endsWith(`/${categoryPart}.service.json`)
    );
  });
  return matchSectionFile;
};

const buildNewLink = (guide, hash, isLocalViewer = false) => {
  const { tag, path } = guide;
  const hashPart = hash ? `#${toUrlFormat(hash)}` : '';
  const guidePath = tag && !isLocalViewer ? `${path}::${tag}.md` : path;
  const guidePathInMdFormat = guidePath.replace(/ /g, '%20');
  return `${guidePathInMdFormat}${hashPart}`;
};

export const buildTopLevelGuideNewLink = (hash, title, article) => {
  const hashPart = hash ? `#${hash}` : '';
  const link = extractTag(article)
    ? getArticlesSectionGuideWithTagLink(title, article)
    : toUrlFormat(`/${title}/${article.name}`);

  return toUrlFormat(`${link}${hashPart}`);
};

const buildNewLinkOfArticlesSectionGuideHash = (guide, hash) => {
  const hashPart = hash ? toUrlFormat(hash) : '';
  return `${guide.link}${hashPart}`;
};

export const replaceMdLinksSpaces = (url) => decodeURIComponent(url);

const isLinkWithCategory = (link) => link.split('/')[1] === 'guides';

const getLinkWithoutCategory = (link) => {
  const linkWithoutCategory = link.split('/');
  linkWithoutCategory.shift();
  return linkWithoutCategory.join('/');
};

const getTopLevelGuideLink = (fileData, articleUrl, link, hash, fullLink) => {
  let newLink;
  for (const item of fileData.items) {
    const article = item.articles.find(
      (_article) => _article.url === replaceMdLinksSpaces(articleUrl)
    );
    if (article) {
      newLink = buildTopLevelGuideNewLink(hash, item.title, article);
      break;
    }
  }
  return newLink ? replaceMdLinkWithNewLink(fullLink, link, newLink) : fullLink;
};

const getGuideLink = (
  fileData,
  linkWithoutHash,
  filePath,
  hash,
  link,
  fullLink
) => {
  const guide = fileData.relatedGuides.find((_guide) => {
    const linkWithoutCategory = isLinkWithCategory(linkWithoutHash)
      ? getLinkWithoutCategory(linkWithoutHash)
      : linkWithoutHash;
    return _guide.path.endsWith(linkWithoutCategory);
  });
  if (guide) {
    const newLink = buildNewLink(guide, hash);
    return replaceMdLinkWithNewLink(fullLink, link, newLink);
  }
  return fullLink;
};

const getHashLink = (guide, link, fullLink) => {
  const isArticlesSectionGuide = !!guide.link;
  const newLink = isArticlesSectionGuide
    ? buildNewLinkOfArticlesSectionGuideHash(guide, link)
    : buildNewLink(guide, link.replace('#', ''));

  return replaceMdLinkWithNewLink(fullLink, link, newLink);
};

const getRelevantLinkAndHash = (link) =>
  getRelativeMdLinkWithoutPathTraversal(replaceMdLinksSpaces(link)).split('#');

export const countOccurrences = (string, pattern) => {
  const patternRegex = new RegExp(pattern, 'g');
  const matches = string.match(patternRegex);
  return matches ? matches.length : 0;
};

export const replacePathTraversal = (urlWithoutHash, currentGuidePath) => {
  const PREVIOUS_FOLDER_PATTERN = /\.\.\//;
  const previousFolderCount = countOccurrences(
    urlWithoutHash,
    PREVIOUS_FOLDER_PATTERN
  );

  const pathWithoutPrefix = currentGuidePath.replace('../guides/', '');
  const isMdFromSameSection =
    countOccurrences(pathWithoutPrefix, '/') > previousFolderCount;

  if (isMdFromSameSection) {
    const currentGuidePathKeys = currentGuidePath.split('/');
    for (let i = 0; i < previousFolderCount + 1; i++) {
      currentGuidePathKeys.pop();
    }

    return `${currentGuidePathKeys.join(
      '/'
    )}/${getRelativeMdLinkWithoutPathTraversal(urlWithoutHash)}`;
  }
};

const getApiGuideLink = (
  currentServiceJson,
  guidePath,
  link,
  hash,
  fullLink,
  isLocalViewer = false
) => {
  const guide = currentServiceJson.relatedGuides.find(
    (_guide) => _guide.path === replaceMdLinksSpaces(guidePath)
  );

  if (guide) {
    const newLink = buildNewLink(guide, hash, isLocalViewer);
    return replaceMdLinkWithNewLink(fullLink, link, newLink);
  }
};

const getLinkFromSameServiceJsonIfNeeded = (
  currentServiceJson,
  currentGuide,
  link,
  fullLink,
  isLocalViewer = false
) => {
  const [linkWithoutHash, hash] = link.split('#');
  const currentGuidePath = currentGuide.path || currentGuide.url;
  const guidePath = replacePathTraversal(linkWithoutHash, currentGuidePath);
  if (guidePath) {
    const isArticlesSectionGuide = !!currentServiceJson.items;
    return isArticlesSectionGuide
      ? getTopLevelGuideLink(
          currentServiceJson,
          guidePath,
          link,
          hash,
          fullLink
        )
      : getApiGuideLink(
          currentServiceJson,
          guidePath,
          link,
          hash,
          fullLink,
          isLocalViewer
        );
  }
};

const replaceMdLinkWithNewLink = (fullLink, link, newLink) => {
  const isReferenceStyleLink = fullLink.includes(
    FLAGS.REFERENCE_STYLE_LINKS_PREFIX
  );
  const prefix = isReferenceStyleLink
    ? `${FLAGS.REFERENCE_STYLE_LINKS_PREFIX} `
    : FLAGS.MD_LINKS_PREFIX;
  return fullLink.replace(`${prefix}${link}`, `${prefix}${newLink}`);
};

const splitLinkTitleIfNeeded = (link) => {
  /*
    extract the link in case of link with title,
    e.g: [Link to something awesome](../my-guide.md "Check out my guide")
  */
  const isLinkIncludesTitle = link.includes('"');
  if (isLinkIncludesTitle) {
    link = link.split('"')[0].trim();
  }
  return link;
};
const setNotReplaceFlagOnLink = (content, regex) =>
  content.replace(regex, (fullLink, desc, link) => {
    link = splitLinkTitleIfNeeded(link);
    const [linkWithoutHash, hash] = link.split('#');
    const hashPart = hash ? `#${hash}` : '';
    const newLink = `${linkWithoutHash}${FLAGS.DO_NOT_REPLACE}${hashPart}`;
    if (isRelativeLinkToMdFile(link)) {
      return replaceMdLinkWithNewLink(fullLink, link, newLink);
    }
    return fullLink;
  });

export const setFlagOnCodeBlocksLinks = (content) =>
  content.replace(REGEXES.ALL_CODE_BLOCKS, (codeBlock) =>
    REGEXES.MD_LINKS_ARRAY.reduce(
      (acc, regex) => setNotReplaceFlagOnLink(acc, regex),
      codeBlock
    )
  );

const removeLinksFlags = (content) => {
  return content.replace(REGEXES.DO_NOT_REPLACE, '');
};

const getNewLinkOfLocalViewer = (
  serviceJsonsData,
  currentGuide,
  link,
  fullLink,
  isLocalViewer
) => {
  const newLink = getLinkFromSameServiceJsonIfNeeded(
    serviceJsonsData[0],
    currentGuide,
    link,
    fullLink,
    isLocalViewer
  );
  return newLink || fullLink;
};

const getNewLink = (
  content,
  currentFilePath,
  serviceJsonPaths,
  serviceJsonsData,
  currentGuide,
  link,
  fullLink
) => {
  const [linkWithoutHash, hash] = getRelevantLinkAndHash(link);
  const newLink = getLinkFromSameServiceJsonIfNeeded(
    serviceJsonsData[currentFilePath],
    currentGuide,
    link,
    fullLink
  );

  if (newLink) {
    return newLink;
  }

  const filePath =
    getRelevantFileByUrl(serviceJsonPaths, linkWithoutHash, currentFilePath) ||
    currentFilePath;

  const fileData = serviceJsonsData[filePath];
  if (fileData) {
    const isArticlesSectionGuide = !!fileData.items;
    if (isArticlesSectionGuide) {
      const articleUrl = `./${linkWithoutHash.replace('guides/', '')}`;
      return getTopLevelGuideLink(fileData, articleUrl, link, hash, fullLink);
    } else {
      return getGuideLink(
        fileData,
        linkWithoutHash,
        filePath,
        hash,
        link,
        fullLink
      );
    }
  }
  return fullLink;
};

const getNewContent = (
  content,
  currentFilePath,
  serviceJsonPaths,
  serviceJsonsData,
  currentGuide,
  findLinksBy,
  isLocalViewer
) =>
  content.replace(findLinksBy, (fullLink, desc, link) => {
    if (isRelativeLinkToMdFile(link)) {
      link = splitLinkTitleIfNeeded(link);
      if (isHashLink(link)) {
        return getHashLink(currentGuide, link, fullLink);
      }
      return isLocalViewer
        ? getNewLinkOfLocalViewer(
            serviceJsonsData,
            currentGuide,
            link,
            fullLink,
            isLocalViewer
          )
        : getNewLink(
            content,
            currentFilePath,
            serviceJsonPaths,
            serviceJsonsData,
            currentGuide,
            link,
            fullLink
          );
    }
    return fullLink;
  });

export const transformRelativeMdLinks = (
  {
    content,
    currentFilePath,
    serviceJsonPaths,
    serviceJsonsData,
    currentGuide,
  },
  isLocalViewer = false
) => {
  try {
    const contentWithFlagsOnCodeBlockLinks = setFlagOnCodeBlocksLinks(content);
    const newContent = REGEXES.MD_LINKS_ARRAY.reduce(
      (_content, regex) =>
        getNewContent(
          _content,
          currentFilePath,
          serviceJsonPaths,
          serviceJsonsData,
          currentGuide,
          regex,
          isLocalViewer
        ),
      contentWithFlagsOnCodeBlockLinks
    );

    return removeLinksFlags(newContent);
  } catch (e) {
    console.error(
      `Error while trying to transformRelativeMdLinks for guide ${currentGuide.path}::`,
      e
    );
    return content;
  }
};
